import { create } from 'zustand';
import type { AuthTokens, AuthUser, LoginInput, Role } from '@tcrd/shared';
import { api, refreshSession, tokens } from '../lib/api';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: Status;
  user: AuthUser | null;
  bootstrap(): Promise<void>;
  login(input: LoginInput): Promise<AuthUser>;
  /** "Continuar con Google" (ID token de Google Identity Services). */
  loginWithGoogle(credential: string, driverCode?: string): Promise<AuthUser>;
  register(input: Record<string, unknown>): Promise<AuthUser>;
  logout(): Promise<void>;
  reloadUser(): Promise<void>;
  hasRole(...roles: Role[]): boolean;
}

export const useAuth = create<AuthState>((set, get) => {
  tokens.onChange((authenticated) => {
    if (!authenticated) set({ status: 'anonymous', user: null });
  });

  const loadMe = async () => {
    const user = await api<AuthUser>('/auth/me');
    set({ status: 'authenticated', user });
    return user;
  };

  return {
    status: 'loading',
    user: null,

    async bootstrap() {
      // Recuperar la sesión desde la cookie (web) o el almacenamiento seguro (Android).
      if (await refreshSession()) {
        try {
          await loadMe();
          return;
        } catch {
          /* cae a anónimo */
        }
      }
      set({ status: 'anonymous', user: null });
    },

    async login(input) {
      tokens.set(await api<AuthTokens>('/auth/login', { body: input }));
      return loadMe();
    },

    async loginWithGoogle(credential, driverCode) {
      tokens.set(await api<AuthTokens>('/auth/google', { body: { credential, driverCode } }));
      return loadMe();
    },

    async register(input) {
      tokens.set(await api<AuthTokens>('/auth/register', { body: input }));
      return loadMe();
    },

    async logout() {
      try {
        const { sessionStorageSecure } = await import('../lib/session-storage');
        const refreshToken = await sessionStorageSecure.getRefresh();
        await api('/auth/logout', { body: refreshToken ? { refreshToken } : {} });
      } finally {
        await tokens.clear();
        set({ status: 'anonymous', user: null });
      }
    },

    async reloadUser() {
      await loadMe();
    },

    hasRole(...roles) {
      const u = get().user;
      if (!u) return false;
      return roles.some((r) => u.roles.includes(r) || (r === 'SUPER_ADMIN' && u.isSuperAdmin));
    },
  };
});

/** Ruta inicial según el rol principal del usuario. */
export function homeFor(user: AuthUser | null): string {
  if (!user) return '/';
  if (user.roles.includes('DRIVER') || user.roles.includes('TENANT_ADMIN')) return '/driver';
  if (user.roles.includes('CUSTOMER')) return '/app';
  if (user.isSuperAdmin) return '/admin';
  return '/';
}
